// Parcel-level validation messages per CALCULATION_MVP.md §13.
//
// These run after per-item calculations have pushed their own messages
// into the sink. We layer on parcel-level concerns (project name, land
// area, at-least-one item, market-price provenance, government-owned
// summary, etc.).

import type { BuildingItem } from "../types/building-item.js";
import type { MarketPriceSet } from "../types/market-price.js";
import type { Parcel } from "../types/parcel.js";

import type { MessageSink } from "./message-sink.js";

/**
 * Run all parcel-level checks. Pure function; mutates the supplied
 * MessageSink rather than returning a new list.
 */
export function validateParcel(
  parcel: Parcel,
  prices: MarketPriceSet,
  items: BuildingItem[],
  sink: MessageSink,
): void {
  if (!parcel.projectName || parcel.projectName.trim() === "") {
    sink.error(
      "projectName",
      "请填写项目名称。",
      "项目名称用于在结果中标识本次测算的宗地。",
    );
  }
  if (!Number.isFinite(parcel.landArea) || parcel.landArea <= 0) {
    sink.error("landArea", "土地面积必须大于 0。");
  }
  if (!items || items.length === 0) {
    sink.error(
      "items",
      "至少需要一项建筑面积分项。",
      "请添加至少一个建筑面积分项。",
    );
  }

  // Per CALCULATION_MVP.md §13 rule 13: market prices must be
  // externally sourced — surface as an info note when a source is
  // missing.
  if (!prices.priceSource || prices.priceSource.trim() === "") {
    sink.info(
      "priceSource",
      "市场价格由外部评估或主管部门数据确定，请确认价格来源已记录。",
    );
  }

  // Per RULES_CORE.md §3 and §7: market prices must not be auto-generated.
  // We surface this once as an info-level reminder at parcel level.
  sink.info(
    "marketPrice",
    "市场价格由用户输入或外部数据导入，系统不自动生成。",
  );
}
